import React, { useState, useEffect } from 'react';
import { Share2, Copy, Check, Gift, Users, Award, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { generateReferralCode, getReferralsCount } from '../services/referralService';
import { Button } from '../components/common/Button';
import { motion } from 'framer-motion';

const Referrals: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referralCode, setReferralCode] = useState('');
  const [referralsCount, setReferralsCount] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      setReferralCode(generateReferralCode(user.id));
      loadReferralsCount();
    }
  }, [user]);

  const loadReferralsCount = async () => {
    if (!user) return;
    const count = await getReferralsCount(user.id);
    setReferralsCount(count);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(`Join ANI Marketplace with my referral code: ${referralCode}\n${window.location.origin}/signup?ref=${referralCode}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join ANI Marketplace',
          text: `Use my referral code: ${referralCode} and get special rewards!`,
          url: `${window.location.origin}/signup?ref=${referralCode}`,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 pb-24">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 flex items-center gap-4 sticky top-0 z-10">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/profile')}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={24} />
        </motion.button>
        <h1 className="text-xl font-bold text-gray-800">Refer & Earn</h1>
      </div>

      <div className="p-4">
        {/* Hero Card */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600 rounded-3xl p-8 text-white mb-6 shadow-2xl relative overflow-hidden"
        >
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-white rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10">
            <Gift className="mb-4" size={56} />
            <h2 className="text-3xl font-bold mb-3">Invite Friends & Earn!</h2>
            <p className="opacity-90 mb-6 text-lg">
              Share your unique code and get rewards when friends join ANI
            </p>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-5 mb-4 border border-white/30">
              <p className="text-sm opacity-90 mb-2 font-medium">Your Referral Code</p>
              <div className="flex items-center justify-between">
                <p className="text-3xl font-black tracking-wider">{referralCode}</p>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={handleCopy}
                  className="p-3 bg-white/30 backdrop-blur-sm rounded-xl hover:bg-white/40 transition-colors"
                >
                  {copied ? <Check size={24} /> : <Copy size={24} />}
                </motion.button>
              </div>
            </div>

            <Button 
              onClick={handleShare} 
              fullWidth 
              className="bg-white text-purple-600 hover:bg-gray-100 font-bold py-4 shadow-xl"
            >
              <Share2 size={20} className="mr-2" />
              Share Your Code
            </Button>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-lg"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-3">
              <Users className="text-white" size={24} />
            </div>
            <p className="text-3xl font-bold text-gray-800">{referralsCount}</p>
            <p className="text-sm text-gray-600">Total Referrals</p>
          </motion.div>
          
          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 shadow-lg"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-3">
              <Award className="text-white" size={24} />
            </div>
            <p className="text-3xl font-bold text-gray-800">₹0</p>
            <p className="text-sm text-gray-600">Rewards Earned</p>
          </motion.div>
        </div>

        {/* How it Works */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-6 shadow-lg"
        >
          <h3 className="font-bold text-gray-800 mb-6 text-xl">How it Works</h3>
          
          <div className="space-y-6">
            {[
              { step: 1, title: 'Share Your Code', desc: 'Send your unique referral code to friends', icon: Share2, color: 'from-blue-500 to-blue-600' },
              { step: 2, title: 'Friend Signs Up', desc: 'They register using your code', icon: Users, color: 'from-purple-500 to-purple-600' },
              { step: 3, title: 'Earn Rewards', desc: 'Get credits when they make purchases', icon: Gift, color: 'from-green-500 to-emerald-600' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="flex gap-4"
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${item.color} text-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                  <item.icon size={24} />
                </div>
                <div>
                  <p className="font-bold text-gray-800 mb-1">{item.title}</p>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Referrals;